import axios from 'axios';

/**
 * Service for fetching version information from the API
 */
class VersionService {
    constructor() {
        this.cache = {
            version: null,
            timestamp: null,
            cacheDuration: 5 * 60 * 1000 // 5 minutes cache
        };
    }

    /**
     * Get the latest version from the API with caching
     * @param {string} baseURL - The base URL for API calls
     * @param {Object} tokens - Optional authentication tokens
     * @returns {Promise<string>} The app version from API or fallback to REACT_APP_VERSION
     */
    async getLatestVersion(baseURL, tokens = null) {
        try {
            // Check if we have a valid cached version
            if (this.cache.version && this.cache.timestamp) {
                const now = Date.now();
                if (now - this.cache.timestamp < this.cache.duration) {
                    return this.cache.version;
                }
            }

            // Prepare headers
            const headers = {
                'Content-Type': 'application/json'
            };
            
            // Add authentication headers if tokens are provided
            if (tokens) {
                const authToken = tokens.authorization;
                const tokenWithBearer = authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`;
                headers['authorization'] = tokenWithBearer;
                headers['refresh'] = tokens.refresh;
                headers['id'] = tokens.id;
            }
            
            // Make the API call - ensure no double slashes
            const apiUrl = `${baseURL.replace(/\/$/, '')}/latestVersion`;
            const response = await axios.get(apiUrl, { headers });
            
            if (response.data && response.data.code === 201 && response.data.data) {
                const version = response.data.data.appVersion;
                
                // Cache the result
                this.cache.version = version;
                this.cache.timestamp = Date.now();
                
                return version;
            } else {
                throw new Error('Invalid response format from /latestVersion API');
            }
        } catch (error) {
            // Return fallback version from environment variable
            return process.env.REACT_APP_VERSION || '1.0.0';
        }
    }

    /**
     * Get version with fallback - tries API first, then falls back to env variable
     * @param {string} baseURL - The base URL for API calls
     * @param {Object} tokens - Optional authentication tokens
     * @returns {Promise<{version: string, source: 'api' | 'env'}>}
     */
    async getVersionWithFallback(baseURL, tokens = null) {
        try {
            const version = await this.getLatestVersion(baseURL, tokens);
            return {
                version,
                source: version === process.env.REACT_APP_VERSION ? 'env' : 'api'
            };
        } catch (error) {
            return {
                version: process.env.REACT_APP_VERSION || '1.0.0',
                source: 'env'
            };
        }
    }

    /**
     * Clear the version cache
     */
    clearCache() {
        this.cache.version = null;
        this.cache.timestamp = null;
    }


}

// Export singleton instance
const versionService = new VersionService();
export default versionService;
